/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
import { MutationFunction } from "@apollo/client";
import * as yup from "yup";
import {
  RemoveApplicantFromList,
  RemoveApplicantFromListVariables,
  RemoveApplicantFromListWithActivity,
  RemoveApplicantFromListWithActivityVariables,
  SaveAsManuallyContactedWithActivity,
  SaveAsManuallyContactedWithActivityVariables,
  SaveAsManuallyContactedWithoutActivity,
  SaveAsManuallyContactedWithoutActivityVariables,
  SaveManualContactActivity,
  SaveManualContactActivityVariables,
  FetchManualContactsView_interestList_scheduledUpdate_manualContacts_results as RequestedResult,
} from "types/gql-types";

import { Options } from "components/haven-design/snackbar/use-snackbar";
import { applicantDataSchema } from "../schema";
import { FormValues } from "./index";

export enum Disposition {
  NoLog,
  NoAnswer,
  NotInService,
  Voicemail,
  Conversation,
  Representative,
  LeftMessage,
  Other,
}


export const DISPOSITION_MESSAGE: Record<Disposition, string> = {
  [Disposition.NoLog]: "No call to log",
  [Disposition.NoAnswer]: "No answer",
  [Disposition.NotInService]: "Applicant phone number appeared to be disconnected",
  [Disposition.Voicemail]: "Left Voicemail",
  [Disposition.Conversation]: "Spoke to Applicant",
  [Disposition.Representative]: "Spoke to Applicant's Representative",
  [Disposition.LeftMessage]: "Left message with someone else",
  [Disposition.Other]: "Other",
};

const activityMessage = (values: FormValues): string => {
  if (values.disposition !== null && values.notes !== "") {
    return `${values.disposition}. ${values.notes}`;
  }

  if (values.disposition !== null) {
    return `${values.disposition}.`;
  }

  return "";
};

interface ActivityInput {
  type: string;
  title: string;
  note: string;
  resource_table: "interests";
  resource_id: string;
}

type AttemptType = MutationFunction<SaveManualContactActivity,
SaveManualContactActivityVariables>;

type UpdateTypeWithActivity = MutationFunction<SaveAsManuallyContactedWithActivity,
SaveAsManuallyContactedWithActivityVariables>;

type UpdateTypeWithoutActivity = MutationFunction<SaveAsManuallyContactedWithoutActivity,
SaveAsManuallyContactedWithoutActivityVariables>;

type RemoveTypeWithActivity = MutationFunction<RemoveApplicantFromListWithActivity,
RemoveApplicantFromListWithActivityVariables>;

type RemoveTypeWithoutActivity = MutationFunction<RemoveApplicantFromList,
RemoveApplicantFromListVariables>;

const submitFunction =
(
  interestId: string,
  nextApplicant: string | null,
  setNextApplicant: (value: string | null) => void,
  setViewingById: (value: string | null) => void,
  submitAttempt: AttemptType,
  submitUpdateWithActivity: UpdateTypeWithActivity,
  submitUpdateWithoutActivity: UpdateTypeWithoutActivity,
  submitRemoveWithActivity: RemoveTypeWithActivity,
  submitRemoveWithoutActivity: RemoveTypeWithoutActivity,
  openSnackbar: (options: Options) => void,
// eslint-disable-next-line max-params
) =>
  (formValues: FormValues) : Promise<void> => {
    const updateViewing = () => {
      setViewingById(nextApplicant);
      setNextApplicant(null);
    };
    const interest = interestId;
    const message = activityMessage(formValues);
    const hasActivity = message !== DISPOSITION_MESSAGE[Disposition.NoLog];
    const isUpdate = formValues.result === "update";
    const isRemove = formValues.result === "remove";
    let successMessage = "Successfully logged a call";
    if (isUpdate) {
      successMessage = "Successfully kept applicant on waitlist";
    } else if (isRemove) {
      successMessage = "Removed applicant";
    }
    const activity: ActivityInput = {
      type: "note",
      title: "Waitlist update manual contact attempt",
      note: message,
      resource_table: "interests",
      resource_id: interestId,
    };

    const submitAction = () => {
      if (isUpdate) {
        const updateVariables = { interest, data: applicantDataSchema.cast(formValues.applicant) };
        return hasActivity ? (
          submitUpdateWithActivity({ variables: { ...updateVariables, activity } })) : (
          submitUpdateWithoutActivity({ variables: updateVariables }));
      } else if (isRemove) {
        const removeVariables = { interest, reason: formValues.removalReason };
        return hasActivity ? (
          submitRemoveWithActivity({ variables: { ...removeVariables, activity } })) : (
          submitRemoveWithoutActivity({ variables: removeVariables }));
      } else if (hasActivity) {
        return submitAttempt({ variables: { activity } });
      }
      return Promise.resolve(null);
    };

    return submitAction()
      .then(() => openSnackbar({ message: successMessage, status: "success" }))
      .finally(() => {
        updateViewing();
      });
  };

type ReferenceItem = Record<string, {next?: string, previous?: string}>
type FetchApplicants = {
  current: string,
  reference: ReferenceItem
}
export const buildApplicantReference = (applicants: RequestedResult[]): ReferenceItem => {
  const { reference } = applicants.reduce(
    (acc, cur) => ({
      current: cur.interestId,
      reference: {
        ...acc.reference,
        [acc.current]: {
          ...acc.reference[acc.current],
          next: cur.interestId,
        },
        [cur.interestId]: { previous: acc.current } as ReferenceItem,
      },
    }),
    { current: "", reference: {} as ReferenceItem } as FetchApplicants,
  );
  return reference;
};

export const manualContactValidationSchema = yup.object({
  result: yup.string().nullable().oneOf(["update", "remove", null]),
  applicant: yup.object().when("result", (result: (string | null), schema: yup.AnyObjectSchema) => {
    if (result === "update") {
      return schema.shape({
        address1: yup.string(),
        address2: yup.string(),
        city: yup.string(),
        email: yup.string().email(),
        householdCount: yup.number().integer().min(1).required(),
        householdNetIncome: yup.number(),
        primaryPhone: yup.string().validPhoneNumber("Must be a valid 10 digit phone number"),
        state: yup.string(),
        zip: yup.string(),
      });
    }
    return schema;
  }),
  removalReason: yup.string().when("result", (result: (string | null), schema: yup.StringSchema) => {
    if (result === "remove") {
      return schema.required();
    }
    return schema;
  }),
});

export default submitFunction;
